/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable no-console */
import type mapboxgl from "mapbox-gl";
import {
  calculateStripAreas,
  createAndDrawStripLines,
  generateParallelLines,
  type ABLine,
  type StripLineResult,
} from "./parallelLinesLayer";
export const addStripLinesToMap = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  abLine: any,
  offsetDistances: number[],
  polygonGeometry: any, // Field polygon geometry
  sourceId: string = "strip-lines",
  options?: {
    lineColor?: string;
    lineWidth?: number;
    stripOpacity?: number;
    stripColors?: string[];
    showLabels?: boolean;
    onStripLineAdjust?: (data: {
      direction: "left" | "right";
      indexOfStripLine: number;
      action?: "step" | "snap";
      snappedDistance?: number;
    }) => void;
  }
): StripLineResult | null => {
  if (!map.current) {
    return null;
  }

  // Validate abLine structure
  if (!abLine) {
    console.error("❌ abLine is null/undefined");
    return null;
  }

  if (!abLine.geojson?.geometry?.coordinates) {
    console.error("❌ abLine structure is invalid:", {
      hasGeojson: !!abLine.geojson,
      hasGeometry: !!abLine.geojson?.geometry,
      hasCoordinates: !!abLine.geojson?.geometry?.coordinates,
    });
    return null;
  }

  const coordinates = abLine.geojson.geometry.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    console.error("❌ abLine coordinates are invalid:", coordinates);
    return null;
  }

  // Validate polygon geometry
  if (!polygonGeometry) {
    console.error("❌ polygonGeometry is null/undefined");
    return null;
  }

  try {
    // Convert the polygon geometry to the expected format
    let polygon = polygonGeometry.geometry || polygonGeometry;

    if (polygon.features && polygon.features.length > 0) {
      // Extract the actual polygon geometry from the FeatureCollection
      polygon = polygon.features[0].geometry;
    } else {
      console.error("❌ Polygon format not recognized:", polygon);
      return null;
    }

    // Create and draw strip lines
    const result = createAndDrawStripLines(
      map.current,
      abLine,
      offsetDistances,
      polygon,
      sourceId,
      options
    );

    return result;
  } catch (error) {
    console.error("❌ Error in addStripLinesToMap:", error);
    if (error instanceof Error) {
      console.error("❌ Error message:", error.message);
      console.error("❌ Error stack:", error.stack);
    }
    return null;
  }
};

/**
 * Remove strip lines from the map
 * @param map - Mapbox map instance
 * @param sourceId - Base source ID for the layers
 */
export const removeStripLinesFromMap: any = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  sourceId: string = "strip-lines"
) => {
  if (!map.current) return;

  // Clean up event handlers first
  const existingHandlers = (map.current as any)._stripLineHandlers || {};
  if (existingHandlers[sourceId]) {
    try {
      if (existingHandlers[sourceId].clickLeft) {
        map.current.off(
          "click",
          `${sourceId}-controls-left-arrow`,
          existingHandlers[sourceId].clickLeft
        );
      }
      if (existingHandlers[sourceId].clickRight) {
        map.current.off(
          "click",
          `${sourceId}-controls-right-arrow`,
          existingHandlers[sourceId].clickRight
        );
      }
      if (existingHandlers[sourceId].clickCircle) {
        map.current.off(
          "click",
          `${sourceId}-controls`,
          existingHandlers[sourceId].clickCircle
        );
      }
      if (existingHandlers[sourceId].mouseenterLeft) {
        map.current.off(
          "mouseenter",
          `${sourceId}-controls-left-arrow`,
          existingHandlers[sourceId].mouseenterLeft
        );
      }
      if (existingHandlers[sourceId].mouseleaveLeft) {
        map.current.off(
          "mouseleave",
          `${sourceId}-controls-left-arrow`,
          existingHandlers[sourceId].mouseleaveLeft
        );
      }
      if (existingHandlers[sourceId].mouseenterRight) {
        map.current.off(
          "mouseenter",
          `${sourceId}-controls-right-arrow`,
          existingHandlers[sourceId].mouseenterRight
        );
      }
      if (existingHandlers[sourceId].mouseleaveRight) {
        map.current.off(
          "mouseleave",
          `${sourceId}-controls-right-arrow`,
          existingHandlers[sourceId].mouseleaveRight
        );
      }
      if (existingHandlers[sourceId].mouseenterCircle) {
        map.current.off(
          "mouseenter",
          `${sourceId}-controls`,
          existingHandlers[sourceId].mouseenterCircle
        );
      }
      if (existingHandlers[sourceId].mouseleaveCircle) {
        map.current.off(
          "mouseleave",
          `${sourceId}-controls`,
          existingHandlers[sourceId].mouseleaveCircle
        );
      }
      if (existingHandlers[sourceId].dividerMouseDown) {
        map.current.off(
          "mousedown",
          `${sourceId}-controls`,
          existingHandlers[sourceId].dividerMouseDown
        );
        map.current.off(
          "mousedown",
          `${sourceId}-controls-divider`,
          existingHandlers[sourceId].dividerMouseDown
        );
      }
      if (existingHandlers[sourceId].dividerTouchStart) {
        map.current.off(
          "touchstart",
          `${sourceId}-controls`,
          existingHandlers[sourceId].dividerTouchStart
        );
        map.current.off(
          "touchstart",
          `${sourceId}-controls-divider`,
          existingHandlers[sourceId].dividerTouchStart
        );
      }
      if (existingHandlers[sourceId].dividerMouseMove) {
        map.current.off(
          "mousemove",
          existingHandlers[sourceId].dividerMouseMove
        );
      }
      if (existingHandlers[sourceId].dividerTouchMove) {
        map.current.off(
          "touchmove",
          existingHandlers[sourceId].dividerTouchMove
        );
      }
      if (existingHandlers[sourceId].dividerMouseUp) {
        map.current.off("mouseup", existingHandlers[sourceId].dividerMouseUp);
      }
      if (existingHandlers[sourceId].dividerTouchEnd) {
        map.current.off("touchend", existingHandlers[sourceId].dividerTouchEnd);
      }

      // Remove general click handler if it exists
      if (existingHandlers[sourceId].generalClick) {
        map.current.off("click", existingHandlers[sourceId].generalClick);
      }
    } catch (error) {
      console.warn(
        "Error removing event handlers in removeStripLinesFromMap:",
        error
      );
    }
    delete existingHandlers[sourceId];
  }

  // Clean up strip hover handlers
  const existingStripHoverHandlers =
    (map.current as any)._stripHoverHandlers || {};
  if (existingStripHoverHandlers[sourceId]) {
    try {
      map.current.off(
        "mouseenter",
        `${sourceId}-strips`,
        existingStripHoverHandlers[sourceId].mouseenter
      );
      map.current.off(
        "mouseleave",
        `${sourceId}-strips`,
        existingStripHoverHandlers[sourceId].mouseleave
      );
    } catch (error) {
      console.warn(
        "Error removing strip hover handlers in removeStripLinesFromMap:",
        error
      );
    }
    delete existingStripHoverHandlers[sourceId];
  }

  const layerIds = [
    `${sourceId}-lines`,
    `${sourceId}-line-labels`,
    `${sourceId}-strips`,
    `${sourceId}-strips-bbox`,
    `${sourceId}-strip-area-labels`,
    `${sourceId}-labels`,
    `${sourceId}-controls`,
    `${sourceId}-controls-divider`,
    `${sourceId}-controls-left-arrow`,
    `${sourceId}-controls-right-arrow`,
  ];

  layerIds.forEach((layerId) => {
    if (map.current?.getLayer(layerId)) {
      map.current.removeLayer(layerId);
    }
  });

  const sourceIds = [
    `${sourceId}-lines-source`,
    `${sourceId}-strips-source`,
    `${sourceId}-strips-bbox-source`,
    `${sourceId}-strip-area-labels-source`,
    `${sourceId}-controls-source`,
    `${sourceId}-controls-divider-source`,
  ];

  sourceIds.forEach((srcId) => {
    if (map.current?.getSource(srcId)) {
      map.current.removeSource(srcId);
    }
  });
};

/**
 * Update strip lines with new offset distances
 * @param map - Mapbox map instance
 * @param abLine - The baseline for strip generation
 * @param offsetDistances - New array of distances in meters
 * @param polygonGeometry - The field polygon
 * @param sourceId - Base source ID for the layers
 * @param options - Styling options
 */
export const updateStripLines = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  abLine: ABLine,
  offsetDistances: number[],
  polygonGeometry: any,
  sourceId: string = "strip-lines",
  options?: {
    lineColor?: string;
    lineWidth?: number;
    stripOpacity?: number;
    stripColors?: string[];
    showLabels?: boolean;
    onStripLineAdjust?: (data: {
      direction: "left" | "right";
      indexOfStripLine: number;
      action?: "step" | "snap";
      snappedDistance?: number;
    }) => void;
  }
): StripLineResult | null => {
  // Remove existing strip lines first
  removeStripLinesFromMap(map, sourceId);

  // Add new strip lines immediately
  const result = addStripLinesToMap(
    map,
    abLine,
    offsetDistances,
    polygonGeometry,
    sourceId,
    options
  );
  return result;
};

/**
 * Toggle strip lines visibility
 * @param map - Mapbox map instance
 * @param sourceId - Base source ID for the layers
 * @param visible - Whether to show or hide the strip lines
 */
export const toggleStripLinesVisibility: any = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  sourceId: string = "strip-lines",
  visible: boolean
) => {
  if (!map.current) return;

  const layerIds = [
    `${sourceId}-lines`,
    `${sourceId}-line-labels`,
    `${sourceId}-strips`,
    `${sourceId}-strips-bbox`,
    `${sourceId}-labels`,
  ];

  layerIds.forEach((layerId) => {
    if (map.current?.getLayer(layerId)) {
      map.current.setLayoutProperty(
        layerId,
        "visibility",
        visible ? "visible" : "none"
      );
    }
  });
};

/**
 * Get strip lines area information
 * @param abLine - The baseline for strip generation
 * @param offsetDistances - Array of distances in meters
 * @param polygonGeometry - The field polygon
 * @returns Area information without rendering to map
 */
export const getStripLinesAreas = (
  abLine: ABLine,
  offsetDistances: number[],
  polygonGeometry: any
): {
  areas: number[];
  totalArea: number;
  stripCount: number;
  lineLengths: number[];
  totalLineLength: number;
} | null => {
  try {
    let polygon = polygonGeometry.geometry || polygonGeometry;

    // Handle FeatureCollection by extracting the first feature's geometry
    if (polygon.features && polygon.features.length > 0) {
      polygon = polygon.features[0].geometry;
    }

    // Generate parallel lines
    const parallelLines = generateParallelLines(
      abLine,
      offsetDistances,
      polygon
    );

    // Calculate strip areas
    const stripResult = calculateStripAreas(parallelLines, polygon);

    const areas = stripResult.strips.map((strip) => strip.area);
    const totalArea = areas.reduce((sum, area) => sum + area, 0);
    const lineLengths = stripResult.lines.map(
      (line) => (line.properties?.length as number) ?? 0
    );
    const totalLineLength = lineLengths.reduce(
      (sum, length) => sum + length,
      0
    );

    return {
      areas,
      totalArea,
      stripCount: stripResult.strips.length,
      lineLengths,
      totalLineLength,
    };
  } catch (error) {
    console.error("Error calculating strip areas:", error);
    return null;
  }
};
